import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useEntitlement } from "@/hooks/useEntitlement";
import { getPaddleEnvironment } from "@/lib/paddle";
import { useEffect, useState } from "react";
import ThemeToggle from "@/components/ThemeToggle";
import BrandName from "@/components/BrandName";
import { cn } from "@/lib/utils";
import { isDemoMode } from "@/lib/demoMode";

const navSections = [
  {
    label: "Control",
    items: [
      { key: "dashboard", label: "Dashboard", icon: "⬡" },
      { key: "content", label: "Content", icon: "📄" },
      { key: "rankings", label: "Rankings", icon: "📈" },
      { key: "integrations", label: "Integrations", icon: "🔌" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { key: "competitors", label: "Competitors", icon: "🎯" },
      { key: "speed", label: "Speed", icon: "⚡" },
      { key: "backlinks", label: "Backlinks", icon: "🔗" },
      { key: "reports", label: "Reports", icon: "📊" },
      { key: "vanta", label: "Vanta AI", icon: "🚀", href: "https://core-logic-panel.lovable.app" },
    ],
  },
  {
    label: "Workspace",
    items: [
      { key: "settings", label: "Settings", icon: "⚙️" },
    ],
  },
];

const TIER_LABELS: Record<string, string> = {
  starter_plan: "Starter Plan",
  pro_plan: "Pro Plan",
  enterprise_plan: "Enterprise Plan",
};

interface Props {
  activeTab: string;
  onTabChange: (tab: string) => void;
  businessName: string | null;
  mobile?: boolean;
  hidden?: boolean;
  onToggleHidden?: () => void;
}

const DashboardSidebar = ({ activeTab, onTabChange, businessName, mobile = false, hidden = false, onToggleHidden }: Props) => {
  const navigate = useNavigate();
  const { isSubscribed, isInTrial, trialDaysLeft, tier, cancelAtPeriodEnd } = useEntitlement();
  const [portalLoading, setPortalLoading] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null);
  const [debugSession, setDebugSession] = useState<{ email: string | null; uid: string | null }>({ email: null, uid: null });

  // Always read the current logged-in user's own client.website_url — never cached/first-row/prop.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { isDemoMode, getDemoClientId } = await import("@/lib/demoMode");
      if (isDemoMode()) {
        const { data: client } = await supabase
          .from("clients").select("website_url").eq("id", getDemoClientId()!).maybeSingle();
        if (!cancelled) {
          setDebugSession({ email: "demo@orbitrank.dev", uid: "demo" });
          setWebsiteUrl((client as any)?.website_url || null);
        }
        return;
      }
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      const email = auth.user?.email ?? null;
      setDebugSession({ email, uid: uid ?? null });
      if (!uid) return;
      const { data: client } = await supabase
        .from("clients")
        .select("user_id,website_url")
        .eq("user_id", uid)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const fresh = client && client.user_id === uid ? (client.website_url || null) : null;
      setWebsiteUrl(fresh);
    })();
    return () => { cancelled = true; };
  }, []);

  const displayName = websiteUrl
    ? websiteUrl.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "")
    : "Loading…";

  const planLabel = isSubscribed
    ? `${TIER_LABELS[tier || ""] || "Active"}${cancelAtPeriodEnd ? " · Cancels soon" : " · Active"}`
    : isInTrial
      ? `Trial · ${trialDaysLeft}d left`
      : "No plan";

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { environment: getPaddleEnvironment() },
      });
      if (error || !data?.overviewUrl) {
        console.error("Portal error:", error);
        return;
      }
      window.open(data.overviewUrl, "_blank");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <aside
      className="flex h-screen flex-col border-r border-border bg-card text-foreground"
      style={{
        position: mobile ? "relative" : "fixed",
        left: mobile ? undefined : 0,
        top: mobile ? undefined : 0,
        zIndex: mobile ? undefined : 50,
        width: 236,
        padding: "22px 13px",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        transform: !mobile && hidden ? "translateX(-100%)" : undefined,
        transition: !mobile ? "transform 700ms ease-in-out" : undefined,
        willChange: !mobile ? "transform" : undefined,
      }}
    >
      {/* Temporary session debug indicator — confirm which user is active */}
      <div className="mb-2 px-2 text-[9px] font-mono text-muted-foreground/60 leading-tight break-all">
        {debugSession.email || "no session"}
        {debugSession.uid ? <><br/><span className="opacity-70">{debugSession.uid}</span></> : null}
      </div>
      <div className="mb-[30px] flex items-center gap-[10px] px-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-primary text-primary-foreground shadow-[0_0_18px_hsl(var(--primary)/0.4)]">
          <svg width="18" height="18" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="planetGrad" cx="38%" cy="35%" r="65%">
                <stop offset="0%" stopColor="#fffdf7"/>
                <stop offset="60%" stopColor="#e8dcc8"/>
                <stop offset="100%" stopColor="#c4a882"/>
              </radialGradient>
            </defs>
            <ellipse cx="50" cy="50" rx="44" ry="15" stroke="#e8dcc8" strokeWidth="3.5" fill="none" transform="rotate(-32 50 50)" strokeOpacity="0.95"/>
            <ellipse cx="50" cy="50" rx="44" ry="15" stroke="#e8dcc8" strokeWidth="3.5" fill="none" transform="rotate(22 50 50)" strokeOpacity="0.95"/>
            <circle cx="50" cy="50" r="14" fill="url(#planetGrad)"/>
            <ellipse cx="44" cy="44" rx="5" ry="3" fill="rgba(255,253,247,0.7)" transform="rotate(-20 44 44)"/>
            <circle cx="83.3" cy="24" r="5.5" fill="#e8dcc8"/>
            <circle cx="83.3" cy="24" r="3" fill="#fffdf7"/>
          </svg>
        </div>
        <BrandName className="text-[15px] font-[800] tracking-[-0.3px]" />
        <div className="ml-auto flex items-center gap-1">
          {!mobile && onToggleHidden && (
            <button
              type="button"
              aria-label="Collapse sidebar"
              onClick={onToggleHidden}
              className="flex h-7 w-7 items-center justify-center rounded-[7px] text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}
          {!isDemoMode() && <ThemeToggle />}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {navSections.map((sec) => (
          <div key={sec.label}>
            <div className="mb-[5px] mt-[14px] px-[10px] text-[9px] font-[700] uppercase tracking-[1.6px] text-muted-foreground/60">
              {sec.label}
            </div>
            {sec.items.map((item) => {
              const isActive = activeTab === item.key;
              if ("href" in item && item.href) {
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "relative mb-[1px] flex w-full items-center gap-[10px] rounded-[9px] border border-transparent px-[10px] py-[9px] text-left text-[13px] font-medium text-muted-foreground transition-all hover:bg-accent/50 hover:text-foreground",
                    )}
                    style={{ textDecoration: "none" }}
                  >
                    <span
                      className={cn(
                        "w-[17px] text-center text-[14px]",
                        item.key === "vanta" && "filter drop-shadow-[0_0_6px_rgba(232,93,58,0.6)] animate-[vantaFloat_3s_ease-in-out_infinite]",
                      )}
                      style={item.key === "vanta" ? { textShadow: "0 0 8px rgba(232,93,58,0.5)" } : undefined}
                    >
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </a>
                );
              }
              return (
                <button
                  key={item.key}
                  data-tour={item.key}
                  onClick={() => onTabChange(item.key)}
                  className={cn(
                    "relative mb-[1px] flex w-full items-center gap-[10px] rounded-[9px] border px-[10px] py-[9px] text-left text-[13px] transition-all",
                    isActive
                      ? "border-primary/20 bg-primary/10 text-primary"
                      : "border-transparent text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                  )}
                  style={{ fontWeight: isActive ? 700 : 500 }}
                >
                  {isActive && (
                    <div className="absolute bottom-[22%] left-0 top-[22%] w-[3px] rounded-r-[3px] bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />
                  )}
                  <span
                    className={cn(
                      "w-[17px] text-center text-[14px]",
                      item.key === "vanta" && "filter drop-shadow-[0_0_6px_rgba(232,93,58,0.6)] animate-[vantaFloat_3s_ease-in-out_infinite]",
                    )}
                    style={item.key === "vanta" ? { textShadow: "0 0 8px rgba(232,93,58,0.5)" } : undefined}
                  >
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <div className="rounded-[12px] border border-primary/20 bg-primary/5 p-[13px_10px]">
          <div className="flex items-center text-[10.5px] font-[800] text-primary">
            <span
              className="mr-[6px] inline-block h-[7px] w-[7px] rounded-full bg-primary"
              style={{ animation: "pulseRing 2s infinite" }}
            />
            SEO ENGINE ACTIVE
          </div>
          <div className="mt-[5px] text-[12px] font-[700] text-foreground">
            {displayName}
          </div>
          <div className="mt-[2px] text-[10px] text-muted-foreground">
            {planLabel}
          </div>

          {isSubscribed ? (
            <button
              onClick={openPortal}
              disabled={portalLoading}
              className="mt-2 w-full rounded-[7px] border border-primary/25 bg-primary/10 py-[6px] text-[10.5px] font-[700] uppercase tracking-wider text-primary transition-all disabled:opacity-50"
            >
              {portalLoading ? "..." : "Manage subscription"}
            </button>
          ) : (
            <button
              onClick={() => navigate("/pricing")}
              className="mt-2 w-full rounded-[7px] bg-primary py-[6px] text-[10.5px] font-[800] uppercase tracking-wider text-primary-foreground transition-all hover:bg-primary/90"
            >
              Upgrade
            </button>
          )}
        </div>

        {debugSession.email?.toLowerCase() === "lorenjordan333@gmail.com" && (
          <button
            onClick={() => navigate("/admin/brain")}
            className="flex w-full items-center gap-2 rounded-[9px] border border-primary/20 bg-primary/5 px-[10px] py-2 text-[12px] font-[700] text-primary transition-all hover:bg-primary/10"
          >
            <span>🧠</span> Brain Center
          </button>
        )}

        <button
          onClick={async () => {
            await supabase.auth.signOut();
            navigate("/auth");
          }}
          className="flex w-full items-center gap-2 rounded-[9px] px-[10px] py-2 text-[12px] text-muted-foreground transition-all hover:bg-accent/40 hover:text-foreground"
        >
          <span>🚪</span> Log out
        </button>
      </div>

      <style>{`
        @keyframes vantaFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-2px) scale(1.08); }
        }
      `}</style>
    </aside>
  );
};

export default DashboardSidebar;
